import {
  MorphCard,
  MorphOpen,
  MorphRest,
  MorphShared,
  useMorphCard,
} from '@/Components/ui/morph-card'
import { cn } from '@/lib/utils'
import type { Partner } from '@/lib/vip-card'
import {
  BADGE,
  BadgeContent,
  CATEGORY,
  DetailRows,
  Foot,
  LOGO_BOX,
  Logo,
  NAME,
  usePartnerFacts,
  type PartnerFacts,
} from './partner-parts'

const SHEET =
  'vip-theme rounded-t-[24px] border-0 bg-card px-5 pb-8 [&>div:first-of-type]:mb-3 [&>div:first-of-type]:mt-3 [&>div:first-of-type]:h-1 [&>div:first-of-type]:w-10 [&>div:first-of-type]:bg-[hsl(var(--muted-foreground)/0.3)]'

/** A partner in the grid, opening to where it is and what its discount carries. */
export function PartnerCard({
  partner,
  provinceCode = null,
}: {
  partner: Partner
  /** The branch to show, when the grid is narrowed to one province. */
  provinceCode?: number | string | null
}) {
  const facts = usePartnerFacts(partner, provinceCode)

  return (
    <MorphCard
      id={String(partner.id)}
      label={facts.name}
      radius={20}
      openRadius={24}
      className="bg-card"
      restClassName="h-[140px] p-6 focus-visible:ring-2 focus-visible:ring-primary"
      openClassName="p-5 dark:ring-1 dark:ring-white/[0.07]"
      drawerClassName={SHEET}
    >
      <PartnerContent facts={facts} />
    </MorphCard>
  )
}

/**
 * The header is shared, so it makes the journey.
 *
 * The logo, the name, the category and the discount are the same elements in
 * both states. The place and the arrow belong to the card; the rows to the
 * panel.
 */
function PartnerContent({ facts }: { facts: PartnerFacts }) {
  const { isOpen, enter } = useMorphCard()

  return (
    <>
      <div className={cn('flex items-start', isOpen ? 'gap-3' : 'gap-4')}>
        <MorphShared
          as="span"
          resize
          data-piece="logo"
          style={{ borderRadius: isOpen ? 12 : 8 }}
          className={cn(
            LOGO_BOX,
            isOpen ? 'size-12' : 'size-[42px]',
            facts.isLogo ? 'p-0' : isOpen ? 'p-3' : 'p-[10px]'
          )}
        >
          <Logo facts={facts} open={isOpen} />
        </MorphShared>

        <span className="flex min-w-0 flex-1 flex-col">
          <MorphShared as="span" data-piece="name" className={NAME}>
            {facts.name}
          </MorphShared>
          <MorphShared as="span" data-piece="category" className={CATEGORY}>
            {facts.category}
          </MorphShared>
        </span>

        <MorphShared as="span" data-piece="badge" className={BADGE}>
          <BadgeContent facts={facts} />
        </MorphShared>
      </div>

      <MorphOpen fade={false} className="pt-4">
        {(present) => <DetailRows facts={facts} shown={present} duration={enter} />}
      </MorphOpen>

      <MorphRest className="absolute inset-x-6 bottom-6 flex items-center justify-between gap-3">
        <Foot facts={facts} />
      </MorphRest>
    </>
  )
}
